import { PropsWithChildren, useCallback, useMemo } from 'react';
import { Button, ButtonProps, Card, IconButton, Stack, Theme, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { add, fromUnixTime, getUnixTime, isPast } from 'date-fns';
import InfoIcon from '@mui/icons-material/Info';
import { userModel } from 'entities/user';
import {
  BORDER_RADIUS_L,
  BORDER_RADIUS_M,
  COLOR_GRADIENT,
  COLOR_NEUTRAL,
  COLOR_NEUTRAL_DARK,
  FontFamilies,
  FontWeights,
  Icon,
  Timer,
  useShallowSelector,
} from 'shared';
import * as dateFns from 'date-fns';
import { useDispatch } from 'react-redux';
import { useWeb3Provider } from '../../../../shared/config/wallet-connect';
import { getFormattedRewardDates } from 'shared/lib';
import _ from 'lodash';

const AIRDROP_COMPLETE_IN_DAYS = 90;
const CLAIM_PRECISION = 2;

const round = (value: number) => _.round(value, CLAIM_PRECISION);

export const AirDropContent = () => {
  const { finish, startTime } = useShallowSelector(userModel.selectors.selectVestingSwapInfo);
  const { totalAmount } = useShallowSelector(userModel.selectors.selectAirdropInfo);

  const airdropCompleteDate = useMemo(() => add(fromUnixTime(+finish), { days: AIRDROP_COMPLETE_IN_DAYS }), [finish]);
  const isAirdropCompleted = useMemo(() => isPast(airdropCompleteDate), [airdropCompleteDate]);

  const [formattedStart, formattedEnd] = getFormattedRewardDates(startTime);

  if (isAirdropCompleted && !totalAmount) return null;

  return (
    <Card component={Stack} spacing={3} sx={{ maxWidth: '100%', padding: 3, mt: 3 }}>
      {isAirdropCompleted ? (
        <Typography sx={{ textAlign: 'center' }}>
          Airdrop has been completed, $5,000 MOGUL reward tokens per NFT have been sent to the wallet where you hold the
          Mogies or Access Pass for 90 days from <b>{formattedStart}</b> to <b>{formattedEnd}</b>.
        </Typography>
      ) : (
        <Typography sx={{ textAlign: 'center' }}>
          Hold Mogies or an Access Pass for at least 90 days between <b>{formattedStart}</b>, and <b>{formattedEnd}</b>{' '}
          and get reward tokens.
          <br />
          Each NFT will entitle the holder to <b>5,000 $MOGUL</b>
        </Typography>
      )}
      {isAirdropCompleted ? (
        <Stack
          spacing={2}
          sx={{
            p: '22px 82px',
            width: '100%',
            m: '0 auto',
            background: COLOR_GRADIENT,
            borderRadius: BORDER_RADIUS_M,
            mt: 1,
          }}
        >
          <Stack direction="row" spacing="11px" alignItems="center" justifyContent="center">
            <Icon type="gift" size={24} />
            <Typography
              className="dark"
              variant="body2"
              fontWeight={FontWeights.Medium}
              fontFamily={FontFamilies.secondary}
              fontSize={16}
            >
              Your bonus:
            </Typography>
          </Stack>
          <Typography className="dark" variant="h2" sx={{ textAlign: 'center' }}>
            {totalAmount} $Mogul
          </Typography>
        </Stack>
      ) : (
        <Stack
          spacing={1}
          alignItems="center"
          justifyContent="center"
          sx={(theme) => ({
            border: `1px solid ${theme.themeColors.colorTimerBorder}`,
            py: '19px',
            borderRadius: BORDER_RADIUS_L,
          })}
        >
          <Timer title="Airdrop will start in" date={getUnixTime(airdropCompleteDate)} onTimerEnds={() => {}} />
        </Stack>
      )}
    </Card>
  );
};

interface ClaimInfoRowProps extends PropsWithChildren {
  title: string;
}

const ClaimInfoRow = ({ title, children }: ClaimInfoRowProps) => {
  const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  return (
    <Stack
      direction={isSmallScreen ? 'column' : 'row'}
      sx={{ justifyContent: 'space-between' }}
      spacing={isSmallScreen ? 0 : 2}
    >
      <Typography color={COLOR_NEUTRAL}>{title}</Typography>
      {children}
    </Stack>
  );
};

interface UnlockInfoTooltipBodyProps {
  unlocksData: { date: string; reward: string }[];
}

const UnlockInfoTooltipBody = ({ unlocksData }: UnlockInfoTooltipBodyProps) => (
  <Stack direction="column" sx={{ padding: 1 }}>
    {unlocksData.map(({ date, reward }) => (
      <Typography variant="caption" color="white" key={date}>
        {reward} MOGUL on {date}
      </Typography>
    ))}
  </Stack>
);

interface ClaimInfoProps extends UnlockInfoTooltipBodyProps {
  totalEarned: number;
  availableToClaim: number;
}

const ClaimInfo = ({ totalEarned, unlocksData, availableToClaim }: ClaimInfoProps) => {
  const nextUnlockData = unlocksData?.[0];

  return (
    <Stack
      spacing={1}
      alignItems="stretch"
      sx={(theme) => ({
        border: `1px solid ${theme.themeColors.colorTimerBorder}`,
        padding: '16px',
        borderRadius: BORDER_RADIUS_L,
        width: '100%',
      })}
    >
      <ClaimInfoRow title="Total Mogul tokens earned:">
        <Typography>{round(totalEarned)} MOGUL</Typography>
      </ClaimInfoRow>
      <ClaimInfoRow title="Next unlock:">
        {unlocksData?.length ? (
          <Stack direction="row" spacing={0.5}>
            <Typography>
              {round(parseFloat(nextUnlockData?.reward))} MOGUL on {nextUnlockData?.date}
            </Typography>
            <Tooltip title={<UnlockInfoTooltipBody unlocksData={unlocksData} />}>
              <IconButton className="transparent" sx={{ width: 24, height: 24 }}>
                <InfoIcon sx={{ color: 'black' }} />
              </IconButton>
            </Tooltip>
          </Stack>
        ) : (
          <Typography>—</Typography>
        )}
      </ClaimInfoRow>
      <ClaimInfoRow title="Available to claim now:">
        <Typography>{round(availableToClaim)} MOGUL</Typography>
      </ClaimInfoRow>
    </Stack>
  );
};

interface ClaimButtonProps extends ButtonProps {
  availableToClaim: number;
}

const ClaimButton = ({ availableToClaim, ...props }: ClaimButtonProps) => (
  <Button
    sx={(theme) => ({
      width: '100%',
      height: 56,
      border: `2px solid ${COLOR_NEUTRAL_DARK}`,
      '&:hover': { border: `2px solid ${COLOR_NEUTRAL_DARK}` },
      '&.Mui-disabled': {
        color: theme.themeColors.colorTimerBorder,
        border: `2px solid ${theme.themeColors.colorTimerBorder}`,
      },
    })}
    variant="outlined"
    disabled={!availableToClaim}
    {...props}
  >
    <b>Claim {availableToClaim} MOGUL</b>
  </Button>
);

const Claim = () => {
  const { vestingRewards, availableToClaimSum } = useShallowSelector(userModel.selectors.selectVestingSwapInfo);

  const dispatch = useDispatch();
  const web3Provider = useWeb3Provider();

  const totalEarned = vestingRewards.reduce((acc, [, reward]) => acc + Number(reward), 0);
  const unlocksData = vestingRewards
    .map(([date, reward]) => ({ date: dateFns.fromUnixTime(date), reward } as { date: Date; reward: string }))
    .filter(({ date, reward }) => dateFns.isAfter(date, new Date()) && reward !== '0')
    .map(({ date, reward }) => ({ date: dateFns.format(date, 'dd.MM.yyyy'), reward }));

  const handleClaim = useCallback(() => {
    dispatch(
      userModel.userActions.claim({
        web3Provider,
      }),
    );
  }, [dispatch, web3Provider]);

  return (
    <Card component={Stack} spacing={2} sx={{ maxWidth: '100%', padding: 3, mt: 3 }}>
      <Typography variant="subtitle1" fontSize={20}>
        Claim Mogul received for Swap
      </Typography>
      <ClaimInfo totalEarned={totalEarned} unlocksData={unlocksData} availableToClaim={availableToClaimSum} />
      <ClaimButton availableToClaim={availableToClaimSum} onClick={handleClaim} />
    </Card>
  );
};

export const AirDrop = () => (
  <Stack>
    <AirDropContent />
    <Claim />
  </Stack>
);
